import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Spacing, Text, Currency, Badge } from '@reservamos/elements';
import snackPlaceholder from 'images/snack-placeholder.webp';
import QuantitySelector from './QuantitySelector';
import useCurrencyConfig from '../../hooks/whitelabel/useCurrencyConfig';

/**
 * SnackPicker component. Display a snack item with its details and quantity selector.
 * @param {object} props - name, description, price, image, quantity, onQuantityChange, isIncreaseDisabled, isDecreaseDisabled, outOfStock.
 * @param {string} props.name - The name of the snack item.
 * @param {string} props.description - The description of the snack item.
 * @param {number} props.price - The price of the snack item.
 * @param {string} [props.image] - Ready image source of the snack item (URL or data URI).
 * @param {number} [props.quantity] - The current quantity of the snack item.
 * @param {function} [props.onQuantityChange] - Callback function when quantity changes.
 * @param {boolean} [props.isIncreaseDisabled] - Indicates if the increase button is disabled.
 * @param {boolean} [props.isDecreaseDisabled] - Indicates if the decrease button is disabled.
 * @param {boolean} [props.outOfStock] - Indicates if the product is out of stock.
 * @returns {JSX.Element} A container with snack item details, image, and quantity selector.
 */
const SnackPicker = ({
  name = 'Papas Sabritas',
  description = 'Bolsa 45 g, variedad de sabores',
  price = 45.0,
  image,
  quantity = 0,
  onQuantityChange,
  isIncreaseDisabled,
  isDecreaseDisabled,
  outOfStock = false,
}) => {
  const { t } = useTranslation('general');
  const { currency, decimals } = useCurrencyConfig();
  /**
   * Updates the quantity by a delta value (e.g., +1 or -1).
   */
  const updateQuantity = (delta) => {
    const newQuantity = Math.max(0, quantity + delta);
    if (onQuantityChange) {
      onQuantityChange(newQuantity);
    }
  };

  return (
    <Spacing alignItems="center" size="S">
      <img
        className="rounded-lg w-16 md:w-24 h-16 md:h-24"
        src={image || snackPlaceholder}
        alt={name}
        height="60"
        width="60"
      />
      <Spacing size="S" vertical fullWidth>
        <Spacing size="XS" vertical>
          <Spacing alignItems="center" justifyContent="space-between" size="XS">
            <Text size="L">{name}</Text>
            {outOfStock && (
              <Badge roundCorners smallPadding type="error">
                <Text color="white" elementType="span" size="XS" weight="semibold">
                  {t('snacks_modal.out_of_stock')}
                </Text>
              </Badge>
            )}
          </Spacing>
          <Text size="XS">{description}</Text>
        </Spacing>
        <Spacing justifyContent="space-between" alignItems="center" size="XS">
          <Currency
            size="L"
            mobileSize="M"
            price={price}
            weight="semibold"
            decimals={decimals}
            currency={currency}
            showCurrency
          />
          <QuantitySelector
            isIncreaseDisabled={isIncreaseDisabled || outOfStock}
            // Being out of stock must not lock the passenger out of removing units already in
            // the cart. `isDecreaseDisabled` already covers the nothing-selected case.
            isDecreaseDisabled={isDecreaseDisabled}
            counter={quantity}
            onClickIncrement={() => updateQuantity(1)}
            onClickDecrement={() => updateQuantity(-1)}
          />
        </Spacing>
      </Spacing>
    </Spacing>
  );
};

SnackPicker.propTypes = {
  name: PropTypes.string,
  description: PropTypes.string,
  price: PropTypes.number,
  image: PropTypes.string,
  quantity: PropTypes.number,
  onQuantityChange: PropTypes.func,
  isIncreaseDisabled: PropTypes.bool,
  isDecreaseDisabled: PropTypes.bool,
  outOfStock: PropTypes.bool,
};

export default SnackPicker;
